import { useLocation } from 'react-router';
import type { PAGE_POSITIONS } from 'types';
import type { Affiliate } from 'types/capi/shared';

import type { Variant } from '~/components/Button';
import { ExternalLink } from '~/components/Button';
import AffiliateLinkClickEvent from '~/events/AffiliateLinkClickEvent';
import useEnv from '~/hooks/useEnv';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type { TrackedButtonClickName } from '~/services/gtm.client';
import wct from '~/services/wct.client';
import type { UrlTemplates } from '~/utils/tracking';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

import Tracked from './Tracked';

type Props = {
  affiliate: Affiliate;
  buttonName?: TrackedButtonClickName;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  pagePosition?: PAGE_POSITIONS;
  urlTemplates: UrlTemplates;
  variant?: Variant;
};

const AffiliatePageLink = ({
  children,
  affiliate,
  variant,
  urlTemplates,
  onClick: onClickProp,
  pagePosition,
  buttonName,
  className
}: Props) => {
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const { clientBrowser, clientIp, isBot, session } = useRootData();
  const { pathname, search } = useLocation();

  const { url, clickRef } = getTrackedUrlAndClickRef(urlTemplates, { user });

  const onClick = async () => {
    onClickProp?.();

    const pagePath = `${env.SITE_BASE_URL}${pathname}${search}`;

    wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage: marketingCookies.getLandingPage() ?? 'unknown',
      pagePath,
      session,
      url
    });

    await new AffiliateLinkClickEvent({
      affiliateName: affiliate.name,
      clientIp,
      isBot,
      isReseller: affiliate.is_reseller,
      pagePosition,
      retailerId: affiliate.id,
      user
    }).send();
  };

  return pagePosition && buttonName ? (
    <Tracked.ExternalLink
      href={url}
      affiliate={affiliate.name}
      target="_blank"
      name={buttonName}
      pagePosition={pagePosition}
      rel="sponsored nofollow noopener"
      variant={variant ?? 'forest'}
      className={className}
      onClick={onClick}
    >
      {children}
    </Tracked.ExternalLink>
  ) : (
    <ExternalLink
      variant={variant ?? 'none'}
      target={'_blank'}
      rel={'sponsored nofollow noopener'}
      href={url}
      onClick={onClick}
      className={className}
    >
      {children}
    </ExternalLink>
  );
};

export default AffiliatePageLink;
